/**
 * Reserved The System employee. Staff holds registered login users only.
 * Keep in lockstep with erp_api/routes/controllers/systemBot.ts.
 */
import { APP_LOGO_SRC } from 'pages/components/LFHeader/LFHeader';

export const SYSTEM_BOT_NAME = 'The System';
export const SYSTEM_BOT_ID_SUFFIX = '-0002';
export const SYSTEM_BOT_EMPLOYEE_ID = '-0002';
export const SYSTEM_BOT_EMPLOYEE_NUMBER_SEQ = 2;
/** Reserved HR invite form created by The System for every organization. */
export const SYSTEM_HR_INVITE_FORM_ID = '-form-hr';
export const SYSTEM_HR_INVITE_FORM_NAME = 'Employee invite';
export const SYSTEM_HR_INVITE_FORM_DESCRIPTION = 'Collects invited employees\' information.';
/** Reserved supplier identification form created by The System for every organization. */
export const SYSTEM_SUPPLIER_INVITE_FORM_ID = '-form-supplier';
export const SYSTEM_SUPPLIER_INVITE_FORM_NAME = 'Supplier invite';
export const SYSTEM_SUPPLIER_INVITE_FORM_DESCRIPTION = 'Collects supplier identification and banking details.';
/** User-facing copy — SYSTEM_BOT_NAME already starts with "The"; never prefix another "The". */
export const SYSTEM_BOT_EMPLOYEE_VIEW_HINT = `${SYSTEM_BOT_NAME} is a built-in employee under the first owner.`;
export const SYSTEM_BOT_EMPLOYEE_CANNOT_CHANGE_MSG = `${SYSTEM_BOT_NAME} is a built-in employee and cannot be changed.`;
export const SYSTEM_BOT_EMPLOYEE_CANNOT_REMOVE_MSG = `${SYSTEM_BOT_NAME} is a built-in employee and cannot be removed.`;
export const SYSTEM_HR_INVITE_FORM_VIEW_HINT = 'The employee invite form is built in for invited employees. It cannot be removed.';
export const SYSTEM_HR_INVITE_FORM_EDIT_HINT = 'The employee invite form is built in and cannot be changed.';
export const SYSTEM_SUPPLIER_INVITE_FORM_VIEW_HINT = 'The supplier invite form is built in for supplier identification. It cannot be removed.';
export const SYSTEM_SUPPLIER_INVITE_FORM_EDIT_HINT = 'The supplier invite form is built in and cannot be changed.';
/** jbdudu987 live row moved from 0002 so the bot can own -0002. */
export const JBDUDU_EMPLOYEE_ID = '-0003';

export const isSystemBotName = (value: unknown): boolean => {
  const normalized = String(value || '').trim().toLowerCase();
  return normalized === 'the system' || normalized === 'system bot';
};

export const isReservedEmployeeNumberSeq = (seq: number): boolean => (
  Number(seq) === SYSTEM_BOT_EMPLOYEE_NUMBER_SEQ
);

export const isSystemBotStaffRow = (row: {
  id?: string | number | null
  username?: string | null
  isSystemBot?: unknown
} | null | undefined): boolean => {
  if (!row) return false;
  if (row.isSystemBot === true || String(row.isSystemBot || '') === 'Yes') return true;
  if (isSystemBotName(row.username)) return true;
  return String(row.id || '').endsWith(SYSTEM_BOT_ID_SUFFIX);
};

export const isSystemBotEmployee = (
  id?: string | null,
  fields?: Record<string, unknown> | null,
  name?: string | null,
): boolean => {
  if (String(id || '') === SYSTEM_BOT_EMPLOYEE_ID) return true;
  if (String(fields?.isSystemBot || '') === 'Yes') return true;
  return isSystemBotName(name);
};

/** Built-in employee kept for internal links — never show in lists, logs, or users. */
export const isHiddenSystemEmployee = isSystemBotEmployee;

export const isHiddenLogActorToken = (token: string | null | undefined): boolean => {
  if (!token) return false;
  if (isSystemBotName(token)) return true;
  if (String(token) === SYSTEM_BOT_EMPLOYEE_ID) return true;
  if (isSystemBotStaffRow({ id: token, username: token })) return true;
  return false;
};

export const isSystemHrInviteForm = (
  id?: string | null,
  _fields?: Record<string, unknown> | null,
): boolean => String(id || '') === SYSTEM_HR_INVITE_FORM_ID;

export const isSystemSupplierInviteForm = (
  id?: string | null,
  _fields?: Record<string, unknown> | null,
): boolean => String(id || '') === SYSTEM_SUPPLIER_INVITE_FORM_ID;

export const isAnySystemForm = (
  id?: string | null,
  fields?: Record<string, unknown> | null,
): boolean => isSystemHrInviteForm(id, fields) || isSystemSupplierInviteForm(id, fields);

/** List/detail photo — built-in system forms use the app logo. */
export const recordRowPhotoSrc = (row: {
  id?: string | null
  kind?: string | null
  fields?: Record<string, unknown> | null
  photoUrl?: string
}): string => (
  isAnySystemForm(row.id, row.fields)
    ? APP_LOGO_SRC
    : String(row.photoUrl || '')
);
